"use client"

import { useState, useContext, useEffect } from "react"
import {
  Container,
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Chip,
  Alert,
  Divider,
} from "@mui/material"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { ErpContext } from "../../context/ErpContext.js"
import { ordenarMenus } from "../../utils/menuAccesoOrden.js"

const GestionarAccesosComponent = () => {
  const [roles, setRoles] = useState([])
  const [rolSeleccionado, setRolSeleccionado] = useState(null)
  const [menuAccesos, setMenuAccesos] = useState([])
  const [componentesSector, setComponentesSector] = useState([])
  const [opcionesAcceso, setOpcionesAcceso] = useState([])
  const [expandedPanels, setExpandedPanels] = useState({})
  const [expandedComponentesSector, setExpandedComponentesSector] = useState({})
  const [accesosOriginales, setAccesosOriginales] = useState([])
  const [accesosFuncionalidadOriginales, setAccesosFuncionalidadOriginales] = useState([])

  const { getRolUsuario, postRolUsuario, updateRolUsuario, getUsuariosByRol, deleteRolYUsuarios, getMenuAcceso, getRolAccesoByIdRol, actualizarRolAccesos, getComponenteSector, getRolSectorAccesoByIdRol, actualizarRolSectorAccesos } = useContext(ErpContext)

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    try {
      const [roles, menu, componentes] = await Promise.all([
        getRolUsuario(),
        getMenuAcceso(),
        getComponenteSector(),
      ])
      setRoles(roles)
      setMenuAccesos(menu)
      setComponentesSector(componentes || [])
    } catch (error) {
      console.error("Error al obtener datos iniciales:", error)
    }
  }

  // Estados extra para crear rol y guardar
  const [nuevoRol, setNuevoRol] = useState("")
  const [creandoRol, setCreandoRol] = useState(false)
  const [crearRolError, setCrearRolError] = useState(null)
  const [crearRolSuccess, setCrearRolSuccess] = useState(null)

  const [guardando, setGuardando] = useState(false)
  const [guardarError, setGuardarError] = useState(null)
  const [guardarSuccess, setGuardarSuccess] = useState(null)
  const [openRolesModal, setOpenRolesModal] = useState(false)
  const [rolOrigen, setRolOrigen] = useState(null)
  const [rolDestino, setRolDestino] = useState(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [usuariosAEliminar, setUsuariosAEliminar] = useState([])
  const [rolAEliminar, setRolAEliminar] = useState(null)

  // Función para construir la estructura jerárquica dinámicamente
  const construirEstructuraJerarquica = (menuAccesos, accesosDelRol, componentesSector, accesosFuncionalidadDelRol) => {
    // console.log("=== CONSTRUCCIÓN DE ESTRUCTURA JERÁRQUICA ===")
    // console.log("componentesSector recibido (raw):", componentesSector)
    // console.log("Cantidad de componentes recibidos:", componentesSector?.length || 0)
    
    const menusFiltrados = menuAccesos.filter((menu) => !menu.eliminado)
    const componentesFiltrados = componentesSector.filter((comp) => !comp.eliminado)

    // console.log("Total menús disponibles:", menusFiltrados.map(m => m.id))
    // console.log("Componentes después de filtrar por eliminado:", componentesFiltrados)
    // console.log("Total componentes sector:", componentesFiltrados.map(c => `${c.id} -> ${c.idMenuAcceso}`))

    // Separar menús principales de submenús
    const menusPrincipales = ordenarMenus(menusFiltrados.filter((menu) => {
      const id = menu.id
      // DASHBOARD es especial (sin hijos)
      if (id === "DASHBOARD") return true
      // Los demás principales no tienen números al final
      return !/\d+$/.test(id) && id !== "DASHBOARD"
    }))
    
    // console.log("Menús identificados como principales:", menusPrincipales.map(m => m.id))

    // Helper para obtener componentes sector de un menú
    const obtenerComponentesSector = (idMenuAcceso) => {
      if (!idMenuAcceso) return []
      
      // Normalizar comparación: trim y comparar strings
      const idMenuNormalizado = String(idMenuAcceso).trim()
      const componentesDelMenu = componentesFiltrados.filter((comp) => {
        const compIdMenu = String(comp.idMenuAcceso || '').trim()
        return compIdMenu === idMenuNormalizado
      })
      
      // console.log(`Componentes sector para "${idMenuAcceso}":`, componentesDelMenu)
      // console.log(`Total componentes filtrados: ${componentesFiltrados.length}, Encontrados: ${componentesDelMenu.length}`)
      
      if (componentesDelMenu.length === 0 && componentesFiltrados.length > 0) {
        console.warn(`⚠️ No se encontraron componentes para "${idMenuAcceso}". Verifica que el campo idMenuAcceso sea exactamente "${idMenuAcceso}"`)
        // console.log("Componentes disponibles y sus idMenuAcceso:", componentesFiltrados.map(c => ({ id: c.id, idMenuAcceso: c.idMenuAcceso })))
      }
      
      return componentesDelMenu.map((comp) => {
        const tieneAcceso = accesosFuncionalidadDelRol.some(
          (a) => {
            const aIdMenu = String(a.idMenuAcceso || '').trim()
            const aIdSector = String(a.idSector || '').trim()
            const compId = String(comp.id || '').trim()
            return aIdMenu === idMenuNormalizado && aIdSector === compId && a.eliminado === false
          }
        )
        return {
          id: comp.id,
          label: comp.descripcion || comp.id, // Usar descripción, o el id como fallback
          tipo: comp.tipo || 'componente',
          activo: tieneAcceso,
          esComponenteSector: true,
        }
      })
    }

    // Construir estructura jerárquica
    const estructuraJerarquica = menusPrincipales.map((menuPrincipal) => {
      let children = []

      // Si no es DASHBOARD, buscar sus hijos
      if (menuPrincipal.id !== "DASHBOARD") {
        const posiblesHijos = menusFiltrados.filter((menu) => {
          // Los hijos tienen el mismo prefijo + número
          const esHijo = menu.id.startsWith(menuPrincipal.id) && /\d+$/.test(menu.id)
          return esHijo
        })

        const hijosOrdenados = ordenarMenus(posiblesHijos, menuPrincipal.id)
        
        // console.log(`Hijos encontrados para ${menuPrincipal.id}:`, posiblesHijos.map(h => h.id))
        
        children = hijosOrdenados.map((child) => {
          const tieneAcceso = accesosDelRol.some((a) => a.idMenuAcceso === child.id && a.eliminado === false)
          // Agregar componentes sector asociados a este submenú
          // console.log(`Buscando componentes sector para submenú: ${child.id}`)
          const componentesSectorChild = obtenerComponentesSector(child.id)
          // console.log(`Componentes encontrados para ${child.id}:`, componentesSectorChild)
          return {
            id: child.id,
            label: child.descripcion,
            activo: tieneAcceso,
            componentesSector: componentesSectorChild,
          }
        })
      }

      // Verificar si el menú principal tiene acceso
      const tieneAccesoMenuPrincipal = accesosDelRol.some(
        (a) => a.idMenuAcceso === menuPrincipal.id && a.eliminado === false,
      )

      // Agregar componentes sector asociados al menú principal
      const componentesSectorPrincipal = obtenerComponentesSector(menuPrincipal.id)

      return {
        id: menuPrincipal.id,
        label: menuPrincipal.descripcion,
        activo: tieneAccesoMenuPrincipal,
        children: children,
        componentesSector: componentesSectorPrincipal,
      }
    })

    return estructuraJerarquica
  }

  useEffect(() => {
    if (rolSeleccionado && menuAccesos.length > 0) {
      cargarAccesosDelRol(rolSeleccionado.id)
    } else {
      setOpcionesAcceso([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolSeleccionado])

  const cargarAccesosDelRol = async (idRol) => {
    try {
      // Asegurar que tengamos los componentes sector cargados
      let componentesActuales = componentesSector
      if (!componentesActuales || componentesActuales.length === 0) {
        componentesActuales = await getComponenteSector()
        setComponentesSector(componentesActuales || [])
      }

      const [accesosDelRol, accesosFuncionalidadDelRol] = await Promise.all([
        getRolAccesoByIdRol(idRol),
        getRolSectorAccesoByIdRol(idRol),
      ])
      // console.log("Accesos del rol:", accesosDelRol)
      // console.log("Accesos de funcionalidad del rol:", accesosFuncionalidadDelRol)
      // console.log("Componentes sector disponibles:", componentesActuales)

      // Guardar accesos originales para comparación posterior
      setAccesosOriginales(accesosDelRol)
      setAccesosFuncionalidadOriginales(accesosFuncionalidadDelRol || [])

      // Construir estructura jerárquica dinámicamente
      const estructuraJerarquica = construirEstructuraJerarquica(
        menuAccesos,
        accesosDelRol,
        componentesActuales || [],
        accesosFuncionalidadDelRol || []
      )
      // console.log("Estructura jerárquica construida:", estructuraJerarquica)
      setOpcionesAcceso(estructuraJerarquica)
    } catch (error) {
      console.error("Error al obtener accesos del rol:", error)
    }
  }

  const handleToggleAccesoMenuPrincipal = (menuId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        if (menu.id === menuId) {
          const nuevoEstado = !menu.activo
          // Si se activa el menú principal, activar todos los hijos
          // Si se desactiva el menú principal, desactivar todos los hijos
          const childrenActualizados = menu.children.map((child) => ({
            ...child,
            activo: nuevoEstado,
          }))

          return {
            ...menu,
            activo: nuevoEstado,
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleToggleAccesoChild = (menuId, childId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        if (menu.id === menuId) {
          const childrenActualizados = menu.children.map((child) =>
            child.id === childId ? { ...child, activo: !child.activo } : child,
          )

          // Si se activa un hijo, activar también el menú principal
          // Si se desactiva un hijo, verificar si quedan hijos activos para mantener el padre activo
          const algunHijoActivo = childrenActualizados.some((child) => child.activo)

          return {
            ...menu,
            activo: algunHijoActivo, // El padre solo está activo si hay al menos un hijo activo
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleToggleComponenteSector = (idMenuAcceso, componenteId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        // Verificar si es el menú principal
        if (menu.id === idMenuAcceso) {
          const componentesActualizados = (menu.componentesSector || []).map((comp) =>
            comp.id === componenteId ? { ...comp, activo: !comp.activo } : comp
          )
          return {
            ...menu,
            componentesSector: componentesActualizados,
          }
        }
        // Buscar en los children
        const childrenActualizados = menu.children.map((child) => {
          if (child.id === idMenuAcceso) {
            const componentesActualizados = (child.componentesSector || []).map((comp) =>
              comp.id === componenteId ? { ...comp, activo: !comp.activo } : comp
            )
            return {
              ...child,
              componentesSector: componentesActualizados,
            }
          }
          return child
        })
        // Si se modificaron los children, retornar el menu actualizado
        if (JSON.stringify(childrenActualizados) !== JSON.stringify(menu.children)) {
          return {
            ...menu,
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }))
  }

  const contarAccesosActivos = (menu) => {
    const menuPrincipalActivo = menu.activo ? 1 : 0
    const hijosActivos = menu.children.filter((child) => child.activo).length
    const componentesSectorActivos = (menu.componentesSector || []).filter((comp) => comp.activo).length
    return menuPrincipalActivo + hijosActivos + componentesSectorActivos
  }

  const prepararDatosParaGuardar = () => {
    const accesosParaGuardar = []
    const accesosFuncionalidadParaGuardar = []

    // Obtener todos los IDs de accesos actuales (activos)
    const accesosActivosActuales = new Set()
    const accesosFuncionalidadActivosActuales = new Set()

    opcionesAcceso.forEach((menu) => {
      // Agregar menú principal si está activo
      if (menu.activo) {
        accesosActivosActuales.add(menu.id)
        accesosParaGuardar.push({
          id: menu.id,
          activo: true,
        })
      }

      // Agregar componentes sector del menú principal
      (menu.componentesSector || []).forEach((comp) => {
        const key = `${menu.id}:${comp.id}`
        if (comp.activo) {
          accesosFuncionalidadActivosActuales.add(key)
          accesosFuncionalidadParaGuardar.push({
            idMenuAcceso: menu.id,
            idSector: comp.id,
            activo: true,
          })
        }
      })

      // Agregar hijos activos
      menu.children.forEach((child) => {
        if (child.activo) {
          accesosActivosActuales.add(child.id)
          accesosParaGuardar.push({
            id: child.id,
            activo: true,
          })
        }

        // Agregar componentes sector de los hijos
        (child.componentesSector || []).forEach((comp) => {
          const key = `${child.id}:${comp.id}`
          if (comp.activo) {
            accesosFuncionalidadActivosActuales.add(key)
            accesosFuncionalidadParaGuardar.push({
              idMenuAcceso: child.id,
              idSector: comp.id,
              activo: true,
            })
          }
        })
      })
    })

    // Comparar con accesos originales para encontrar los que se desactivaron
    accesosOriginales.forEach((accesoOriginal) => {
      // Si el acceso estaba activo antes pero ahora no está en los activos actuales
      if (!accesoOriginal.eliminado && !accesosActivosActuales.has(accesoOriginal.idMenuAcceso)) {
        accesosParaGuardar.push({
          id: accesoOriginal.idMenuAcceso,
          activo: false, // Marcar como inactivo para eliminar
        })
      }
    })

    // Comparar con accesos funcionalidad originales para encontrar los que se desactivaron
    accesosFuncionalidadOriginales.forEach((accesoFuncionalidadOriginal) => {
      const key = `${accesoFuncionalidadOriginal.idMenuAcceso}:${accesoFuncionalidadOriginal.idSector}`
      if (!accesoFuncionalidadOriginal.eliminado && !accesosFuncionalidadActivosActuales.has(key)) {
        accesosFuncionalidadParaGuardar.push({
          idMenuAcceso: accesoFuncionalidadOriginal.idMenuAcceso,
          idSector: accesoFuncionalidadOriginal.idSector,
          activo: false,
        })
      }
    })

    return { accesosParaGuardar, accesosFuncionalidadParaGuardar }
  }

  const handleGuardarCambios = async () => {
    if (!rolSeleccionado?.id) return;
    const { accesosParaGuardar, accesosFuncionalidadParaGuardar } = prepararDatosParaGuardar()
    setGuardando(true)
    setGuardarError(null)
    setGuardarSuccess(null)
    try {
      await Promise.all([
        actualizarRolAccesos(rolSeleccionado.id, accesosParaGuardar),
        actualizarRolSectorAccesos(rolSeleccionado.id, accesosFuncionalidadParaGuardar),
      ])
      setGuardarSuccess("Accesos actualizados correctamente")
      await cargarAccesosDelRol(rolSeleccionado.id)
    } catch (error) {
      setGuardarError("Error al actualizar accesos")
    } finally {
      setGuardando(false)
    }
  }

  // Función para determinar el estado del checkbox padre
  const getParentCheckboxState = (menu) => {
    const hijosActivos = menu.children.filter((child) => child.activo).length
    const totalHijos = menu.children.length

    if (totalHijos === 0) return menu.activo // Para menús sin hijos (DASHBOARD)
    if (hijosActivos === 0) return false
    if (hijosActivos === totalHijos) return true
    return "indeterminate" // Estado intermedio cuando algunos hijos están activos
  }

  

  return (
    <Container maxWidth="lg">
     
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Gestión de Accesos por Rol
        </Typography>
        <Button variant="outlined" size="small" onClick={() => setOpenRolesModal(true)}>Administrar roles</Button>
      </Box>

       {/* Crear nuevo rol - arriba de todo */}
       <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
        <TextField
          size="small"
          label="Nuevo rol"
          value={nuevoRol}
          onChange={(e) => setNuevoRol(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          disabled={creandoRol || !nuevoRol.trim()}
          onClick={async () => {
            setCreandoRol(true)
            setCrearRolError(null)
            setCrearRolSuccess(null)
            try {
              const creado = await postRolUsuario(nuevoRol.trim())
              const rolesActualizados = await getRolUsuario()
              setRoles(rolesActualizados || [])
              const rolCreado = rolesActualizados?.find((r) => r.id === creado.id) || null
              setRolSeleccionado(rolCreado)
              setCrearRolSuccess("Rol creado correctamente")
              setNuevoRol("")
            } catch (e) {
              setCrearRolError("No se pudo crear el rol")
            } finally {
              setCreandoRol(false)
            }
          }}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          {creandoRol ? <CircularProgress size={20} color="inherit" /> : "Crear rol"}
        </Button>
      </Box>
      {crearRolError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCrearRolError(null)}>
          {crearRolError}
        </Alert>
      )}
      {crearRolSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCrearRolSuccess(null)}>
          {crearRolSuccess}
        </Alert>
      )}

      <Divider
        variant="fullWidth"
        sx={{
          my: 3,
          borderBottomWidth: 3,
          borderColor: "rgba(0, 0, 0, 0.25)",
          mx: { xs: -2, sm: -3, md: -4 },
        }}
      />

      <Autocomplete
        size="small"
        options={roles}
        getOptionLabel={(option) => option.descripcion}
        value={rolSeleccionado}
        onChange={(e, value) => setRolSeleccionado(value)}
        renderInput={(params) => <TextField {...params} label="Seleccionar Rol" fullWidth />}
        sx={{ marginBottom: 3 }}
      />

      {/* Crear nuevo rol (duplicado eliminado, ahora va arriba) */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, display: 'none' }}>
        <TextField
          size="small"
          label="Nuevo rol"
          value={nuevoRol}
          onChange={(e) => setNuevoRol(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          disabled={creandoRol || !nuevoRol.trim()}
          onClick={async () => {
            setCreandoRol(true)
            setCrearRolError(null)
            setCrearRolSuccess(null)
            try {
              const creado = await postRolUsuario(nuevoRol.trim())
              const rolesActualizados = await getRolUsuario()
              setRoles(rolesActualizados || [])
              const rolCreado = rolesActualizados?.find((r) => r.id === creado.id) || null
              setRolSeleccionado(rolCreado)
              setCrearRolSuccess("Rol creado correctamente")
              setNuevoRol("")
            } catch (e) {
              setCrearRolError("No se pudo crear el rol")
            } finally {
              setCreandoRol(false)
            }
          }}
          sx={{ textTransform: "none" }}
        >
          {creandoRol ? <CircularProgress size={20} color="inherit" /> : "Crear rol"}
        </Button>
      </Box>
      {crearRolError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCrearRolError(null)}>
          {crearRolError}
        </Alert>
      )}
      {crearRolSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCrearRolSuccess(null)}>
          {crearRolSuccess}
        </Alert>
      )}

      {rolSeleccionado && (
        <Paper elevation={2} sx={{ padding: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                Accesos para rol: <strong>{rolSeleccionado.descripcion}</strong>
              </Typography>
              <Chip
                label={`${opcionesAcceso.reduce((total, menu) => total + contarAccesosActivos(menu), 0)} accesos activos`}
                color="primary"
                size="small"
              />
            </Box>
            <span />
          </Box>


          {opcionesAcceso.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No se encontraron opciones de acceso para este rol.
            </Alert>
          )}

          {opcionesAcceso.map((menu) => {
            const parentState = getParentCheckboxState(menu)

            return (
              <Accordion
                key={menu.id}
                expanded={expandedPanels[menu.id] || false}
                onChange={handleAccordionChange(menu.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={(menu.children.length > 0 || (menu.componentesSector && menu.componentesSector.length > 0)) ? <ExpandMoreIcon /> : null}
                  sx={{
                    backgroundColor: menu.activo ? "rgba(25, 118, 210, 0.08)" : "inherit",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={parentState === true}
                          indeterminate={parentState === "indeterminate"}
                          onChange={() => handleToggleAccesoMenuPrincipal(menu.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={menu.activo ? 600 : 400}>
                            {menu.label}
                          </Typography>
                          {menu.children.length > 0 && (
                            <Chip
                              label={`${menu.children.filter((child) => child.activo).length}/${menu.children.length}`}
                              size="small"
                              color={menu.children.some((child) => child.activo) ? "primary" : "default"}
                            />
                          )}
                          {menu.componentesSector && menu.componentesSector.length > 0 && (
                            <Chip
                              label={`${menu.componentesSector.filter((comp) => comp.activo).length}/${menu.componentesSector.length} componentes`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>

                {(menu.children.length > 0 || (menu.componentesSector && menu.componentesSector.length > 0)) && (
                  <AccordionDetails sx={{ pt: 0 }}>
                    {menu.children.length > 0 && (
                      <FormGroup sx={{ pl: 2, mb: menu.componentesSector && menu.componentesSector.length > 0 ? 2 : 0 }}>
                        {menu.children.map((child) => (
                          <Box key={child.id} sx={{ mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={child.activo}
                                  onChange={() => handleToggleAccesoChild(menu.id, child.id)}
                                  size="small"
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    color={child.activo ? "primary" : "text.secondary"}
                                    fontWeight={child.activo ? 500 : 400}
                                  >
                                    {child.label}
                                  </Typography>
                                  {child.componentesSector && child.componentesSector.length > 0 && (
                                    <Chip
                                      label={`${child.componentesSector.filter((comp) => comp.activo).length}/${child.componentesSector.length} componentes`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              }
                              sx={{ ml: 1 }}
                            />
                            {child.componentesSector && child.componentesSector.length > 0 && (
                              <Accordion 
                                expanded={expandedComponentesSector[`${child.id}-sector`] || false}
                                onChange={(e, isExpanded) => setExpandedComponentesSector(prev => ({ ...prev, [`${child.id}-sector`]: isExpanded }))}
                                sx={{ ml: 4, mt: 0.5, boxShadow: 'none', '&:before': { display: 'none' } }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{ minHeight: 32, py: 0 }}
                                >
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Componentes sector ({child.componentesSector.filter((comp) => comp.activo).length}/{child.componentesSector.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                  <FormGroup>
                                    {child.componentesSector.map((comp) => (
                                      <FormControlLabel
                                        key={comp.id}
                                        control={
                                          <Checkbox
                                            checked={comp.activo || false}
                                            onChange={() => handleToggleComponenteSector(child.id, comp.id)}
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                              variant="caption"
                                              color={comp.activo ? "secondary.main" : "text.secondary"}
                                              fontWeight={comp.activo ? 500 : 400}
                                            >
                                              {comp.label || comp.descripcion || comp.id}
                                            </Typography>
                                            {comp.tipo && (
                                              <Chip
                                                label={comp.tipo}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 18, fontSize: '0.65rem' }}
                                              />
                                            )}
                                          </Box>
                                        }
                                        sx={{ ml: 1 }}
                                      />
                                    ))}
                                  </FormGroup>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        ))}
                      </FormGroup>
                    )}
                    {menu.componentesSector && menu.componentesSector.length > 0 && (
                      <Accordion
                        expanded={expandedComponentesSector[`${menu.id}-sector`] || false}
                        onChange={(e, isExpanded) => setExpandedComponentesSector(prev => ({ ...prev, [`${menu.id}-sector`]: isExpanded }))}
                        sx={{ 
                          ml: 2, 
                          mt: menu.children.length > 0 ? 1 : 0,
                          borderTop: menu.children.length > 0 ? '1px solid #e0e0e0' : 'none',
                          pt: menu.children.length > 0 ? 1 : 0,
                          boxShadow: 'none',
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ minHeight: 40 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.primary" fontWeight={600}>
                              Componentes sector
                            </Typography>
                            <Chip
                              label={`${menu.componentesSector.filter((comp) => comp.activo).length}/${menu.componentesSector.length} activos`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FormGroup>
                            {menu.componentesSector.map((comp) => (
                              <FormControlLabel
                                key={comp.id}
                                control={
                                  <Checkbox
                                    checked={comp.activo}
                                    onChange={() => handleToggleComponenteSector(menu.id, comp.id)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color={comp.activo ? "secondary.main" : "text.secondary"}
                                      fontWeight={comp.activo ? 500 : 400}
                                    >
                                      {comp.label || comp.descripcion || comp.id}
                                    </Typography>
                                    {comp.tipo && (
                                      <Chip
                                        label={comp.tipo}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                }
                                sx={{ ml: 1 }}
                              />
                            ))}
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            )
          })}

          <Box sx={{ marginTop: 3, display: "flex", gap: 2, alignItems: "center" }}>
            <Button variant="contained" onClick={handleGuardarCambios} disabled={guardando}>
              {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar cambios"}
            </Button>
            <Button variant="outlined" onClick={() => cargarAccesosDelRol(rolSeleccionado.id)} disabled={guardando}>
              Recargar
            </Button>
            {guardarError && (
              <Alert severity="error" onClose={() => setGuardarError(null)} sx={{ ml: 2 }}>
                {guardarError}
              </Alert>
            )}
            {guardarSuccess && (
              <Alert severity="success" onClose={() => setGuardarSuccess(null)} sx={{ ml: 2 }}>
                {guardarSuccess}
              </Alert>
            )}
          </Box>
        </Paper>
      )}

      {/* Modal de administración de roles (siempre montado) */}
      <Dialog open={openRolesModal} onClose={() => setOpenRolesModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Administración de Roles</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Editar descripciones</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {roles.map((r) => (
              <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #eee' }}>
                <TextField size="small" label={`Rol ${r.id}`} value={r.descripcion}
                  onChange={(e) => {
                    const val = e.target.value
                    setRoles((prev) => prev.map((x) => x.id === r.id ? { ...x, descripcion: val } : x))
                  }}
                />
                <Button size="small" variant="outlined" onClick={async () => {
                  try {
                    await updateRolUsuario(r.id, r.descripcion)
                    const rs = await getRolUsuario()
                    setRoles(rs || [])
                  } catch {
                    setGuardarError("No se pudo actualizar la descripción del rol")
                  }
                }}>Guardar</Button>
                <Button size="small" color="error" variant="outlined" onClick={async () => {
                  try {
                    setGuardarError(null)
                    setUsuariosAEliminar([])
                    setRolAEliminar(r)
                    const lista = await getUsuariosByRol(r.id)
                    setUsuariosAEliminar(lista || [])
                    setConfirmDeleteOpen(true)
                  } catch {
                    setGuardarError("No se pudieron obtener usuarios del rol")
                  }
                }}>Eliminar</Button>
              </Box>
            ))}
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Copiar accesos</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">Rol origen:</Typography>
            <Autocomplete
              size="small"
              options={roles}
              getOptionLabel={(opt) => opt.descripcion}
              value={rolOrigen}
              onChange={(e, val) => setRolOrigen(val)}
              renderInput={(params) => <TextField {...params} label="Rol origen" />}
              sx={{ minWidth: 260 }}
            />
            <Typography variant="body2" sx={{ ml: 2 }}>Rol destino:</Typography>
            <Autocomplete
              size="small"
              options={roles}
              getOptionLabel={(opt) => opt.descripcion}
              value={rolDestino}
              onChange={(e, val) => setRolDestino(val)}
              renderInput={(params) => <TextField {...params} label="Rol destino" />}
              sx={{ minWidth: 260 }}
            />
            <Button size="small" variant="contained" disabled={!rolOrigen || !rolDestino || guardando} onClick={async () => {
              try {
                setGuardando(true)
                setGuardarError(null)
                setGuardarSuccess(null)
                const accesosOrigen = await getRolAccesoByIdRol(rolOrigen.id)
                const activosSet = new Set(accesosOrigen.filter(a => a.eliminado === false).map(a => a.idMenuAcceso))
                const payload = menuAccesos.map(m => ({ id: m.id, activo: activosSet.has(m.id) }))
                await actualizarRolAccesos(rolDestino.id, payload)
                setGuardarSuccess("Accesos copiados correctamente")
                if (rolSeleccionado?.id === rolDestino.id) {
                  await cargarAccesosDelRol(rolDestino.id)
                }
              } catch {
                setGuardarError("No se pudieron copiar los accesos")
              } finally {
                setGuardando(false)
              }
            }}>Copiar</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRolesModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación de rol y usuarios */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Eliminar rol y usuarios asociados</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Vas a eliminar el rol <strong>{rolAEliminar?.descripcion}</strong> y los siguientes usuarios quedarán marcados como eliminados:
          </Typography>
          {usuariosAEliminar.length === 0 ? (
            <Alert severity="info">No se encontraron usuarios activos con este rol.</Alert>
          ) : (
            <Box sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
              {usuariosAEliminar.map((u) => (
                <Typography key={u.id} variant="body2">• {u.usuario} - {u.nombre} {u.apellido}</Typography>
              ))}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción realiza un borrado lógico (eliminado = true) de los usuarios listados y del rol.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={!rolAEliminar || guardando} onClick={async () => {
            try {
              setGuardando(true)
              setGuardarError(null)
              setGuardarSuccess(null)
              await deleteRolYUsuarios(rolAEliminar.id)
              setGuardarSuccess("Rol y usuarios eliminados correctamente")
              const rs = await getRolUsuario()
              setRoles(rs || [])
              // Si el rol eliminado es el seleccionado, limpiar selección y vista
              if (rolSeleccionado?.id === rolAEliminar.id) {
                setRolSeleccionado(null)
                setOpcionesAcceso([])
              }
              setConfirmDeleteOpen(false)
              setOpenRolesModal(false)
            } catch {
              setGuardarError("No se pudo eliminar el rol")
            } finally {
              setGuardando(false)
            }
          }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default GestionarAccesosComponent
